import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, TrendingUp, Users } from 'lucide-react';
import { PlanCard } from './components/PlanCard';
import { PaymentInstructions } from './components/PaymentInstructions';
import { subscriptionService } from '../../services/subscriptionService';
import { SubscriptionPlan } from './types';

export default function SubscriptionPlans() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const data = await subscriptionService.getPlans();
      setPlans(data);
    } catch (err) {
      setError('Failed to load subscription plans. Please try again.');
      console.error('Error loading plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (planId: string) => {
    setSelectedPlanId(planId);
    setError(null);
  };

  const handleContinue = () => {
    if (selectedPlanId) {
      const selectedPlan = plans.find((p) => p.id === selectedPlanId);
      if (selectedPlan?.plan_type === 'free') {
        navigate('/register', { state: { planId: selectedPlanId } });
      } else {
        navigate('/payment-processing', { state: { planId: selectedPlanId } });
      }
    }
  };

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Perfect Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Start with our free plan or unlock premium features for your community
            contribution tracking
          </p>
        </div>

        {/* Trust Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <Shield className="w-10 h-10 text-blue-600 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Secure Payments</h3>
            <p className="text-sm text-gray-600">
              Bank transfer with admin verification for your security
            </p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <TrendingUp className="w-10 h-10 text-green-600 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Flexible Plans</h3>
            <p className="text-sm text-gray-600">
              Monthly subscriptions that grow with your community needs
            </p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <Users className="w-10 h-10 text-purple-600 mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Community First</h3>
            <p className="text-sm text-gray-600">
              Built for communities, trusted by thousands of users
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isSelected={selectedPlanId === plan.id}
              onSelect={handleSelectPlan}
              isProcessing={isProcessing}
            />
          ))}
        </div>

        {/* Payment Instructions (shown when premium/enterprise selected) */}
        {selectedPlan && selectedPlan.plan_type !== 'free' && (
          <div className="mb-8">
            <PaymentInstructions selectedPlanPrice={selectedPlan.price_vatu} />
          </div>
        )}

        {/* Continue Button */}
        {selectedPlanId && (
          <div className="text-center">
            <button
              onClick={handleContinue}
              disabled={isProcessing}
              className="bg-blue-600 text-white px-12 py-4 rounded-xl font-semibold text-lg hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {selectedPlan?.plan_type === 'free' ?'Continue to Registration' :'Continue to Payment'}
            </button>
          </div>
        )}

        {/* Comparison Table */}
        <div className="mt-16 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Compare All Features
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">
                      Feature
                    </th>
                    {plans.map((plan) => (
                      <th
                        key={plan.id}
                        className="text-center py-4 px-6 font-semibold text-gray-900"
                      >
                        {plan.plan_name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 text-gray-700">Monthly Price</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="text-center py-4 px-6 font-semibold">
                        {plan.price_vatu === 0 ? 'Free' : `${plan.price_vatu} VT`}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 text-gray-700">Group Members</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="text-center py-4 px-6">
                        {plan.max_group_members || 'Unlimited'}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 text-gray-700">Monthly Contributions</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="text-center py-4 px-6">
                        {plan.max_contributions_per_month || 'Unlimited'}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 text-gray-700">Reporting</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="text-center py-4 px-6 capitalize">
                        {plan.features.reporting}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 text-gray-700">Support</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="text-center py-4 px-6 capitalize">
                        {plan.features.support}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="py-4 px-6 text-gray-700">Storage</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="text-center py-4 px-6">
                        {plan.features.storage}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">
                How does the payment process work?
              </h3>
              <p className="text-gray-600">
                After selecting a paid plan, transfer the amount via bank transfer using the
                provided details. Upload your payment proof, and our admin team will verify
                and activate your subscription within 1-2 business days.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">
                What happens when my subscription expires?
              </h3>
              <p className="text-gray-600">
                When your subscription expires, your access will be locked. You will receive
                notifications before expiry. Simply renew your subscription to restore full
                access to your account and data.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">
                Can I upgrade or downgrade my plan?
              </h3>
              <p className="text-gray-600">
                Yes! You can change your plan at any time. Upgrades take effect immediately
                upon payment approval, while downgrades apply at your next billing cycle.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}